import { format, parseISO } from 'date-fns';

const STATUS = {
  INACTIVE: 'inactive',
  PENDING: 'pending',
  RECEIVED: 'order received',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  CANCELED: 'canceled'
};

const STATUS_BY_ORDER_TASK_STATUS = {
  canceled: STATUS.CANCELED,
  cancelled: STATUS.CANCELED,
  declined: STATUS.PENDING,
  incomplete: STATUS.RECEIVED,
  pending: STATUS.RECEIVED,
  processing: STATUS.PENDING,
  action_req: STATUS.PENDING,
  active: STATUS.ACTIVE,
  complete: STATUS.COMPLETE
};

export const getDerivedStatus = (status) => (
  status ? STATUS_BY_ORDER_TASK_STATUS[status] : STATUS.INACTIVE
);

export function getOrderYear(obj) {
  const date = new Date(obj.createdDatetime);
  return date.getFullYear();
}

export function getUniqueOrders(orderTasks) {
  const unique = [];

  orderTasks.forEach((task) => {
    const year = getOrderYear(task);
    const { orderUuid } = task;

    const isUnique = !unique.some((item) => item.year === year && item.order === orderUuid);
    if (isUnique) {
      unique.push({ year, order: orderUuid });
    }
  });

  return unique;
}

export function getUniqueYears(obj) {
  const years = obj.map((order) => order.year);
  const uniqueSet = new Set(years);
  return Array.from(uniqueSet).sort((a, b) => b - a);
}

function sortPlans(unsorted) {
  return unsorted.sort((a, b) => new Date(b.createdDatetime) - new Date(a.createdDatetime));
}

function sortByYearIfSelected(unsorted, year) {
  if (year === '') {
    return sortPlans(unsorted);
  }
  const rows = unsorted.filter((item) => getOrderYear(item) === year);
  return sortPlans(rows);
}

export function getFormattedDate(date) {
  return date ? format(parseISO(date), 'MMMM dd, yyyy') : 'Not Yet Available';
}

export function correctDisplaySource(task) {
  const values = {
    date: getFormattedDate(task.createdDatetime),
    price: task.amount,
    displayName: task.displayName
  };

  if (task.orderTaskType === 'llc_formation') {
    return {
      ...values,
      price: task.fee != null ? `${task.fee}.00` : '0.00',
      displayName: `State Filing Fee - ${task.orderTaskJurisdiction}`
    };
  }

  if (task.orderTaskType === 'registered_agent_service') {
    return { ...values, displayName: `${task.displayName} - ${task.orderTaskJurisdiction}` };
  }

  return values;
}

export const transactionalBundles = [
  'ein_business_docs_bundle'
];

const includedInTransactionalBundles = new Map([
  ['ein_business_docs_bundle', ['ein_creation', 'corporate_docs']]
]);

export function filterTransactionBundles(orderTasks) {
  const result = [];
  const used = [];

  const bundles = orderTasks.filter((task) => transactionalBundles.includes(task.orderTaskType));
  if (bundles.length === 0) {
    return null;
  }

  bundles.forEach((bundle) => {
    const bundled = includedInTransactionalBundles.get(bundle.orderTaskType);
    const included = [];
    for (const item of bundled) {
      const ot = orderTasks.find((task) => task.orderTaskType === item);
      if (ot) {
        included.push(ot);
        used.push(ot);
      }
    }
    result.push({ ...bundle, included, isTransactional: true });
    used.push(bundle);
  });

  return { transactionalBundles: result, used };
}

export function groupPlanWithTasksByYear(orderTasks, paymentHistory, selectedYear) {
  const rows = [];
  const usedTasks = [];

  let remainingOrderTasks = orderTasks;
  const transactionalItems = filterTransactionBundles(orderTasks);

  if (transactionalItems && transactionalItems.transactionalBundles.length > 0) {
    const { transactionalBundles: bundles, used } = transactionalItems;
    bundles.forEach((bundle) => rows.push(bundle));
    used.forEach((task) => usedTasks.push(task));

    remainingOrderTasks = orderTasks.filter(
      (task) => used.findIndex((item) => item?.orderTaskType === task?.orderTaskType) === -1
    );
  }

  if (!paymentHistory || !Array.isArray(paymentHistory)) {
    return rows;
  }

  paymentHistory.forEach((payment) => {
    if (!['price', 'fee'].includes(payment.chargeType)) {
      return;
    }

    const { includedItems } = payment;

    if (includedItems) {
      const ot = remainingOrderTasks.find((item) => item?.orderItemUuid === payment?.orderItemUuid);
      if (ot) {
        const isUsed = usedTasks.find((t) => t?.orderTaskUuid === ot?.orderTaskUuid);
        if (isUsed) {
          usedTasks.push(ot);
          return;
        }
        usedTasks.push(ot);
        const { includedItems: paymentIncludedItems, ...paymentWithoutIncludedItems } = payment;
        const result = { ...ot, ...paymentWithoutIncludedItems };

        result.included = [];

        if (paymentIncludedItems) {
          paymentIncludedItems.forEach((included) => {
            const task = remainingOrderTasks.find(
              (t) => t?.orderItemUuid === included?.orderItemUuid
            );
            if (task) {
              if (task.orderTaskType === 'llc_formation') {
                usedTasks.push(task);
                rows.push({ ...task, ...included });
                return;
              }
              usedTasks.push(task);
              result.included.push({ ...task });
            }
          });
        }
        rows.push(result);
      }
    }
  });

  paymentHistory.forEach((payment) => {
    const leftover = usedTasks.some((task) => task?.orderItemUuid === payment?.orderItemUuid);
    if (!leftover) {
      const ot = remainingOrderTasks.find(
        (item) => item?.orderItemUuid === payment?.orderItemUuid
      );
      let task;
      if (ot) {
        usedTasks.push(ot);
        const { includedItems, ...paymentWithoutIncluded } = payment;
        task = { ...ot, ...paymentWithoutIncluded };
      }
      if (ot && task) {
        rows.push(task);
      }
    }
  });

  orderTasks.forEach((task) => {
    const leftover = usedTasks.some((ot) => task?.orderItemUuid === ot?.orderItemUuid);
    if (!leftover) {
      rows.push((task));
    }
  });

  return sortByYearIfSelected(rows, selectedYear);
}
